import React, { useCallback, useEffect, useState } from 'react';
import { DocumentDuplicateIcon, ListBulletIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import { trainingSessionAPI } from '../../services/api';
import type { TrainingPlan, TrainingPlanItem } from '../../types/trainingSession';
import { useToast } from '../../contexts/ToastContext';
import PlanView from './PlanView';
import PlanBlocksEditor from './PlanBlocksEditor';
import { appendItems, cleanItems, emptyItem, hasUntitledContent, isBlankItem, totalMinutes } from './planUtils';
import TemplatePicker from '../drills/TemplatePicker';
import TemplateFormModal from '../drills/TemplateFormModal';
import { getErrorMessage } from '../../utils/errorMessage';

interface TrainingPlanSectionProps {
  eventId: number;
  /** Szerkesztheti-e (a megtekintéshez elég a stábtagság). */
  canEdit: boolean;
  /** Az edzés korosztálya és hossza - az AI edzésterv ezekkel nyílik meg. */
  ageGroupId?: number | null;
  durationMinutes?: number | null;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Az edzés terve: a fő cél és a sorba rendezett gyakorlat-blokkok (név, perc,
 * leírás, rajz). Betölthető edzéssablonból, és elmenthető sablonként. Csak a
 * stáb látja.
 */
const TrainingPlanSection: React.FC<TrainingPlanSectionProps> = ({ eventId, canEdit, ageGroupId, durationMinutes }) => {
  const { showToast } = useToast();
  const [plan, setPlan] = useState<TrainingPlan | null>(null);
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [goal, setGoal] = useState('');
  const [items, setItems] = useState<TrainingPlanItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [savingAsTemplate, setSavingAsTemplate] = useState(false);

  const load = useCallback(async () => {
    try {
      setPlan(await trainingSessionAPI.getPlan(eventId));
    } catch (err: any) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        setHidden(true);
      } else {
        console.error('Az edzésterv betöltése nem sikerült:', err);
      }
    }
  }, [eventId]);

  useEffect(() => {
    setEditing(false);
    setHidden(false);
    load();
  }, [load]);

  if (hidden || !plan) return null;

  const isEmpty = !plan.goal && plan.items.length === 0;

  const startEditing = () => {
    setGoal(plan.goal || '');
    setItems(plan.items.length > 0 ? plan.items.map((i) => ({ ...i })) : [emptyItem()]);
    setEditing(true);
  };

  const handleLoadTemplate = (template: { name: string; goal?: string | null; items: TrainingPlanItem[] }, mode: 'replace' | 'append') => {
    if (mode === 'replace') {
      const hasContent = goal.trim() || items.some((i) => !isBlankItem(i));
      if (hasContent && !window.confirm(`A „${template.name}” sablon lecseréli a jelenlegi tervet. Folytatod?`)) return;
      setGoal(template.goal || '');
      setItems(template.items.length > 0 ? template.items.map((i) => ({ ...i })) : [emptyItem()]);
    } else {
      setItems((prev) => appendItems(prev, template.items));
      if (!goal.trim() && template.goal) setGoal(template.goal);
    }
    setTemplatePickerOpen(false);
    showToast(`„${template.name}” betöltve. Mentsd el az edzéstervet a véglegesítéshez.`);
  };

  const handleSave = async () => {
    if (hasUntitledContent(items)) {
      showToast('Minden blokknak adj nevet (pl. Bemelegítés).', 'error');
      return;
    }

    setSaving(true);
    try {
      const saved = await trainingSessionAPI.savePlan(eventId, { goal: goal.trim() || null, items: cleanItems(items) });
      setPlan(saved);
      setEditing(false);
      showToast('Edzésterv elmentve.');
    } catch (err: any) {
      showToast(getErrorMessage(err, 'Az edzésterv mentése nem sikerült.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const secondaryButton =
    'px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100';

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
      {templatePickerOpen && (
        <TemplatePicker onClose={() => setTemplatePickerOpen(false)} onLoad={handleLoadTemplate} />
      )}
      {savingAsTemplate && (
        <TemplateFormModal
          template={null}
          initial={{ goal: plan.goal, items: plan.items }}
          onClose={() => setSavingAsTemplate(false)}
          onSaved={(template) => {
            setSavingAsTemplate(false);
            showToast(`„${template.name}” elmentve edzéssablonként.`);
          }}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ListBulletIcon className="h-5 w-5 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">Edzésterv</h4>
          {!editing && totalMinutes(plan.items) > 0 && (
            <span className="text-xs text-gray-500">· {totalMinutes(plan.items)} perc</span>
          )}
        </div>
        {!editing && canEdit && (
          <div className="flex flex-wrap gap-2">
            {!isEmpty && (
              <button type="button" onClick={() => setSavingAsTemplate(true)} className={`${secondaryButton} inline-flex items-center gap-1`}>
                <DocumentDuplicateIcon className="h-4 w-4" />
                Mentés sablonként
              </button>
            )}
            <button type="button" onClick={startEditing} className={secondaryButton}>
              {isEmpty ? 'Edzésterv megadása' : 'Szerkesztés'}
            </button>
          </div>
        )}
      </div>

      {!editing &&
        (isEmpty ? (
          <p className="text-sm text-gray-500">Még nincs edzésterv.</p>
        ) : (
          <PlanView goal={plan.goal} items={plan.items} />
        ))}

      {editing && (
        <div className="space-y-3">
          <div>
            <label htmlFor="training-goal" className="block text-sm font-medium text-gray-700 mb-1">
              Az edzés célja
            </label>
            <input
              id="training-goal"
              type="text"
              maxLength={500}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="pl. Gyors kombinatív játék, átmenetek támadásból védekezésbe"
              className={inputClass}
            />
          </div>

          <PlanBlocksEditor
            items={items}
            setItems={setItems}
            aiDefaults={{ ageGroupId, durationMinutes }}
            onAiGoal={(aiGoal) => {
              if (!goal.trim()) setGoal(aiGoal);
            }}
            extraActions={
              <button
                type="button"
                onClick={() => setTemplatePickerOpen(true)}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline"
              >
                <RectangleStackIcon className="h-4 w-4" />
                Sablon betöltése
              </button>
            }
          />

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">
              {totalMinutes(items) > 0 ? `Összesen ${totalMinutes(items)} perc.` : ''} Csak a stáb látja.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={saving}
                className="flex-1 sm:flex-none px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 sm:flex-none px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Mentés...' : 'Mentés'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingPlanSection;
